import { Box, Card, Link, Section, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import {
  CreateJoinCompanyDocument,
  UpdateAtsCompanyJoinSettingsDocument,
  UpdateAtsCompanyJoinSettingsMutationVariables,
} from 'generated/resources/graphql'

import HelpCenterLink from 'modules/core/components/HelpCenterLink'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useAtsContext } from 'modules/ats/lib/atsContext'

import { ExternalProfiles } from './ExternalProfiles'

type UpdateJoinSettingsParams = Omit<UpdateAtsCompanyJoinSettingsMutationVariables, 'id'>

export const JoinPromotionSettings = () => {
  const { company } = useCurrentSession()
  const { atsCompanyId, joinEnabled, joinDirectApplyEnabled } = useAtsContext()

  const [createIntegrationsJoinCompany, { loading: creatingCompany }] = useMutation(
    CreateJoinCompanyDocument,
    {
      extractorFn: (data) => data.integrationsJoinMutations.createIntegrationsjoinJoinCompany,
    }
  )
  const [updateAtsCompany, { loading: updatingSettings }] = useMutation(
    UpdateAtsCompanyJoinSettingsDocument,
    {
      extractorFn: (data) => data.atsMutations.updateAtsCompany,
    }
  )

  const createJoinCompany = async () => {
    const result = await createIntegrationsJoinCompany({
      variables: {
        companyId: company.id,
      },
    })

    const handled = handleResult(result, (errors) => {
      return app.setFlash({
        type: 'wrong',
        message: errors.messages || i18n.t('common.save_error'),
      })
    })

    if (handled.success) {
      return updateAtsCompanySetting({ joinEnabled: true, joinDirectApplyEnabled: true })
    }

    return handled
  }

  const updateAtsCompanySetting = async (variables: UpdateJoinSettingsParams) => {
    const result = await updateAtsCompany({
      variables: {
        ...variables,
        id: atsCompanyId,
      },
    })

    const handled = handleResult(result, (_errors) => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }

    return handled
  }

  const handleJoinInstallation = (toggleValue: boolean) => {
    if (toggleValue === joinEnabled) return

    if (joinEnabled) {
      // TODO: handle delete company from join only
      return // removeJoinCompany() and updateAtsCompanySetting({ joinEnabled: false })
    }

    createJoinCompany()
  }

  return (
    <>
      <Section
        illustration="network"
        title={i18n.t('ats.settings.job_promotions.title')}
        description={i18n.t('ats.settings.job_promotions.description')}
        banner={
          <HelpCenterLink name="ats_integrations_join_how_to">
            {i18n.t('ats.settings.job_promotions.learn_more.title')}
          </HelpCenterLink>
        }
      >
        <Box gap="s12">
          <Card>
            {/* promote jobs  */}
            <Card.Title
              size="small"
              action={
                <TogglerCheckbox
                  value={joinEnabled}
                  disabled={updatingSettings || creatingCompany || joinEnabled}
                  onChange={handleJoinInstallation}
                />
              }
            >
              {i18n.t('ats.settings.job_promotions.promote_jobs.title')}
            </Card.Title>
            <Card.Description>
              {i18n.t('ats.settings.job_promotions.promote_jobs.description')}
            </Card.Description>
            <Card.Footer>
              <Box>
                <Link.Href href="https://join.com/privacy-policy" target="_blank" external>
                  JOIN privacy policy
                </Link.Href>
              </Box>
            </Card.Footer>
          </Card>
          {joinEnabled && (
            <>
              {/* direct apply  */}
              <Card>
                <Card.Title
                  size="small"
                  action={
                    <TogglerCheckbox
                      value={joinDirectApplyEnabled}
                      disabled={updatingSettings || joinDirectApplyEnabled}
                      onChange={() =>
                        updateAtsCompanySetting({ joinDirectApplyEnabled: !joinDirectApplyEnabled })
                      }
                    />
                  }
                >
                  {i18n.t('ats.settings.job_promotions.direct_apply.title')}
                </Card.Title>
                <Card.Description>
                  {i18n.t('ats.settings.job_promotions.direct_apply.description')}
                </Card.Description>
              </Card>
            </>
          )}
        </Box>
      </Section>

      {joinEnabled && <ExternalProfiles />}
    </>
  )
}
